import { useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { isAuthError } from "@supabase/supabase-js";
import { refusedByTheApi, type InviteKind } from "@shyne/shared/api";
import { Button } from "@/components/ui/button";
import { FormCreator, type FormFieldDef } from "@/components/shared/form-creator";
import { useAppDispatch } from "@/lib/store";
import { logoutUser } from "@/lib/store/auth.slice.ts";
import { settleIntoOrganization } from "@/app/auth/signup/signup.slice.ts";
import { acceptInvite } from "@/app/auth/signup/signup.api.ts";
import { AuthRepository } from "@/repositories/auth.repository.ts";

const NAMES_AN_ORGANIZATION: ReadonlySet<InviteKind> = new Set(["subcontractor", "vendor", "customer"]);

const WHAT_ACCEPTING_DOES: Record<InviteKind, string> = {
  team: "You will join it as a team member.",
  worker: "You will work for it as an individual.",
  subcontractor: "Your company will be its sub-contractor.",
  vendor: "Your company will be its vendor.",
  customer: "Your organization will be its client.",
  vendorRelationship: "",
};

const acceptSchema = z.object({
  name: z.string().trim().optional(),
  phone: z.string().trim().optional(),
  organizationName: z.string().trim().optional(),
  password: z.string().optional(),
  confirm: z.string().optional(),
});

const newPersonSchema = acceptSchema
  .extend({
    password: z.string().min(8, "At least 8 characters").max(72, "At most 72 characters"),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, { path: ["confirm"], message: "Passwords must match" });

type AcceptValues = z.infer<typeof acceptSchema>;

const alreadyThisPassword = (error: unknown): boolean => isAuthError(error) && error.code === "same_password";

async function setOwnPassword(password: string): Promise<void> {
  try {
    await AuthRepository.updatePassword(password);
  } catch (error: unknown) {
    if (!alreadyThisPassword(error)) throw error;
  }
}

export function AcceptInvite({ token, kind, inviterName, email, inviteeName, newPerson }: { token: string; kind: InviteKind; inviterName: string | null; email: string; inviteeName: string | null; newPerson: boolean }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const namesAnOrganization = NAMES_AN_ORGANIZATION.has(kind);

  const form = useForm<AcceptValues>({
    resolver: zodResolver(newPerson ? newPersonSchema : acceptSchema),
    defaultValues: { name: inviteeName ?? "", phone: "", organizationName: "", password: "", confirm: "" },
  });

  const fields: FormFieldDef[] = [
    ...(newPerson
      ? [
          { type: "text", name: "name", label: "Full name", required: true } as FormFieldDef,
          { type: "text", name: "phone", label: "Phone" } as FormFieldDef,
          { type: "password", name: "password", label: "Password", required: true } as FormFieldDef,
          { type: "password", name: "confirm", label: "Confirm password", required: true } as FormFieldDef,
        ]
      : []),
    ...(namesAnOrganization ? [{ type: "text", name: "organizationName", label: "Organization Name", required: true } as FormFieldDef] : []),
  ];

  const accept = async ({ name, phone, organizationName, password }: AcceptValues) => {
    setSaving(true);
    setRefusal(null);
    try {
      if (newPerson && password) await setOwnPassword(password);
      const { organization } = await acceptInvite(token, {
        ...(newPerson && name ? { name } : {}),
        ...(newPerson && phone ? { phone } : {}),
        ...(namesAnOrganization && organizationName ? { organizationName } : {}),
      });
      await dispatch(settleIntoOrganization(organization));
      toast.success(inviterName ? `You joined ${inviterName}.` : "Invitation accepted.");
      navigate("/");
    } catch (error: unknown) {
      const message =
        refusedByTheApi(error) && error.status === 404
          ? `This invitation was sent to ${email}. Sign out, then open the link again to accept it with that email.`
          : error instanceof Error
            ? error.message
            : "The invitation could not be accepted.";
      setRefusal(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4">
        <div className="flex flex-col items-start gap-2">
          <h1 className="text-2xl font-bold">Accept your invitation</h1>
          <p className="text-sm text-muted-foreground">
            {inviterName ?? "An organization"} invited {email} to Shyne. {WHAT_ACCEPTING_DOES[kind]}
          </p>
        </div>
        {refusal && <p className="text-sm text-destructive">{refusal}</p>}
        <FormCreator form={form} formDefinition={fields} buttonText={saving ? "Accepting…" : "Accept invitation"} onSubmit={accept} />
        <Button variant="ghost" className="w-full" onClick={() => dispatch(logoutUser())}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
